import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { FormattedMessage } from 'react-intl';

import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { noop } from 'ui-components/default-props/noop';

import { messages } from './messages';
import style from './rate-and-create.module.less';
import FormattedCurrencyContainer from '../../../../../components/formatted-currency/container';
import RepeatButton from '../../../../../components/repeat-button';
import { CreateLabelButtonContainer } from '../../../../../containers/create-label-button/orders-bulk';
import { emptyArray } from 'ui-components/default-props/empty';
import { CostReviewLink, getShouldDisableCostReview } from '../../../../../fulfillment-planner/cost-review-link';
import { BulkRateRecommendations } from '../../../../../fulfillment-planner/rate-recommendation/recommendation/bulk-rate-recommendations';
import { EmptyRateDisplayValue } from '../../../../../orders/order-details/rate-card/rate/empty-rate-display-value';
import { HAS_DISMISSED_BATCH_VIEW_UPGRADE_PROMO_BANNER } from '../../../../../hooks/use-local-storage';
import { UpgradePlanPromotionalBanner } from '../../../../../components/upgrade-plan-promotional-banner/upgrade-plan-promotional-banner';

/**
 * @typedef {object} OrdersRate
 * @property {string} code - The currency code.
 * @property {number} value - The rate value.
 * @property {string[]} errors - The list of errors.
 * @property {undefined|number} missingCostReviewDataCount - Number of orders missing cost review data.
 */

const defaultOrderRate = {
  value: 0,
  code: '',
  errors: emptyArray,
  missingCostReviewDataCount: undefined,
};

/**
 * @typedef {import('ui-components/components/button').ButtonClickHandler} ButtonClickHandler
 */

/**
 * @param {object} props
 * @param {OrdersRate} props.ordersRate - The rate information for the orders.
 * @param {ButtonClickHandler} props.onGetRates - The function to call to get rates.
 * @param {boolean} props.hasAnyRateErrors - Indicates if there are any rate errors.
 * @param {string[]} props.fulfillmentPlanIds - The list of fulfillment plan IDs.
 * @param {boolean} props.disableRateRefresh
 * @param {boolean} props.isEligibleForBulkRecommendedRates - Whether the user is eligible for bulk recommended
 * @param {boolean} props.isLoadingRates
 * @returns {React.JSX.Element}
 */
export const BulkRateAndCreate = ({
  ordersRate = defaultOrderRate,
  onGetRates = noop,
  hasAnyRateErrors = false,
  fulfillmentPlanIds,
  disableRateRefresh = false,
  isEligibleForBulkRecommendedRates = true,
  isLoadingRates = false,
}) => {
  const shouldDisplayRateValue = ordersRate.value > 0;

  const isCostReviewDisabled = getShouldDisableCostReview({
    isRating: isLoadingRates,
    hasRateErrors: hasAnyRateErrors,
    hasRate: shouldDisplayRateValue && !ordersRate?.missingCostReviewDataCount,
    hasProvidedRate: false,
  });

  return (
    <div className={style['rate-and-create-container']}>
      <div className={style['rate-and-create']}>
        <div className={style.rate}>
          <div className={style.amount} data-testid='rate-container'>
            <FormattedCurrencyContainer currencyCode={ordersRate.code} value={ordersRate.value}>
              {(formattedRate) => (
                <FormattedMessage
                  {...messages.rate}
                  values={{
                    formattedRate: shouldDisplayRateValue ? formattedRate : <EmptyRateDisplayValue />,
                  }}
                />
              )}
            </FormattedCurrencyContainer>
          </div>
          {hasAnyRateErrors ? (
            <Tooltip position='bottom'>
              <TooltipContent>
                <FormattedMessage {...messages.rateWarning} />
              </TooltipContent>
              <div className={style.warning} data-testid='rate-warning-icon'>
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
            </Tooltip>
          ) : null}
          <RepeatButton
            actionHandler={onGetRates}
            className={style['repeat-get-rates']}
            disabled={disableRateRefresh}
            isSpinning={isLoadingRates}
          />
        </div>
        <CostReviewLink fulfillmentPlanIds={fulfillmentPlanIds} linkDisabled={isCostReviewDisabled} />
        <CreateLabelButtonContainer parentComponentName='sidebar bulk selection' />
      </div>
      {isEligibleForBulkRecommendedRates ? (
        <BulkRateRecommendations fulfillmentPlanIds={fulfillmentPlanIds} isLoadingRates={isLoadingRates} />
      ) : null}

      <UpgradePlanPromotionalBanner
        bannerId={HAS_DISMISSED_BATCH_VIEW_UPGRADE_PROMO_BANNER}
        promotionalSubheaderMessage={messages.upgradePromotionalBannerTitle}
        showCloseButton
      />
    </div>
  );
};
